import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Award, CheckCircle, Sparkles, Navigation, MessageCircle, Gift } from 'lucide-react';
import NearULogo from '../components/NearULogo';
import { getFirstName } from '../utils/nameUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MatchesPage = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('top'); // 'top' or 'nearby'
  const [matches, setMatches] = useState([]);
  const [topMatches, setTopMatches] = useState([]);

  useEffect(() => {
    fetchMatches();
  }, [view]);

  const fetchMatches = async () => {
    try {
      if (view === 'top') {
        const response = await axios.get(`${API}/matches/top`);
        setTopMatches(response.data);
        setMatches(response.data);
      } else {
        const response = await axios.get(`${API}/matches/nearby`);
        setMatches(response.data);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
      // Fallback to regular matches
      const response = await axios.get(`${API}/matches`);
      setMatches(response.data);
    }
  };

  const renderIncomeBadge = (incomeVerified) => {
    if (!incomeVerified) return null;
    
    // Map legacy 'silver' to 'gold' for backwards compatibility
    const level = incomeVerified === 'silver' ? 'gold' : incomeVerified;
    
    const badges = {
      gold: { 
        color: '#D4AF37', 
        fillColor: '#D4AF37',
        type: 'coin'
      },
      diamond: { 
        color: '#4169E1', 
        fillColor: '#4169E1',
        type: 'diamond'
      }
    };

    const badge = badges[level];
    if (!badge) return null;

    return (
      <div style={{
        position: 'absolute',
        top: '8px',
        left: '8px',
        background: 'rgba(0, 0, 0, 0.8)',
        padding: '6px 10px',
        borderRadius: '10px',
        border: `2px solid ${badge.color}`,
        boxShadow: `0 4px 12px rgba(212, 175, 55, 0.4)`,
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id={`matchIncome-${level}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: badge.fillColor, stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: badge.fillColor, stopOpacity: 0.8 }} />
            </linearGradient>
          </defs>
          {badge.type === 'coin' ? (
            <>
              <circle 
                cx="12" 
                cy="12" 
                r="9" 
                fill={`url(#matchIncome-${level})`}
                stroke={badge.color}
                strokeWidth="2"
              />
              <circle 
                cx="12" 
                cy="12" 
                r="6" 
                fill="none"
                stroke={badge.color}
                strokeWidth="1"
                opacity="0.5"
              />
            </>
          ) : (
            <>
              {/* Realistic Diamond Stone Shape */}
              {/* Top facets (table and crown) */}
              <path 
                d="M12 4 L8 9 L12 10 L16 9 Z" 
                fill={badge.color}
                opacity="0.9"
                stroke={badge.color}
                strokeWidth="0.5"
              />
              {/* Upper girdle facets */}
              <path 
                d="M8 9 L5 11 L12 11 Z" 
                fill={`url(#matchIncome-${incomeVerified})`}
                stroke={badge.color}
                strokeWidth="0.5"
                opacity="0.8"
              />
              <path 
                d="M16 9 L19 11 L12 11 Z" 
                fill={`url(#matchIncome-${incomeVerified})`}
                stroke={badge.color}
                strokeWidth="0.5"
                opacity="0.8"
              />
              {/* Lower girdle and pavilion */}
              <path 
                d="M5 11 L12 11 L12 20 Z" 
                fill={`url(#matchIncome-${incomeVerified})`}
                stroke={badge.color}
                strokeWidth="1"
              />
              <path 
                d="M19 11 L12 11 L12 20 Z" 
                fill={badge.color}
                stroke={badge.color}
                strokeWidth="1"
                opacity="0.7"
              />
              {/* Culet (bottom point) highlight */}
              <circle 
                cx="12" 
                cy="20" 
                r="0.5" 
                fill="white" 
                opacity="0.6"
              />
              {/* Facet lines for depth */}
              <path 
                d="M8 9 L12 11 M16 9 L12 11 M12 4 L12 11" 
                stroke={badge.color}
                strokeWidth="0.5"
                opacity="0.3"
                fill="none"
              />
            </>
          )}
        </svg>
      </div>
    );
  };

  return (
    <div style={{ paddingBottom: '80px' }}>
      <div className="page-header">
        <div style={{ marginBottom: '12px' }}><NearULogo size="small" showText={true} /></div>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>
          {view === 'top' ? 'TOP MATCHES' : 'NEARBY NOW'}
        </h1>
        <p className="page-subtitle">
          {view === 'top' ? 'AI-powered compatibility matches' : 'People close to you'}
        </p>
      </div>

      {/* View Toggle */}
      <div style={{ padding: '16px', borderBottom: '1px solid #A57C33', background: '#2C1B13' }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setView('top')}
            style={{
              flex: 1,
              padding: '12px',
              background: view === 'top' ? 'linear-gradient(135deg, #D7B56D, #A57C33)' : 'rgba(215, 181, 109, 0.1)',
              color: view === 'top' ? '#2C1B13' : '#D7B56D',
              border: view === 'top' ? 'none' : '1px solid #A57C33',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: view === 'top' ? '0 4px 12px rgba(215, 181, 109, 0.3)' : 'none'
            }}
          >
            <Sparkles size={18} />
            Top Matches
          </button>
          <button
            onClick={() => setView('nearby')}
            style={{
              flex: 1,
              padding: '12px',
              background: view === 'nearby' ? 'linear-gradient(135deg, #D7B56D, #A57C33)' : 'rgba(215, 181, 109, 0.1)',
              color: view === 'nearby' ? '#2C1B13' : '#D7B56D',
              border: view === 'nearby' ? 'none' : '1px solid #A57C33',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: view === 'nearby' ? '0 4px 12px rgba(215, 181, 109, 0.3)' : 'none'
            }}
          >
            <Navigation size={18} />
            Nearby Now
          </button>
        </div>
      </div>

      {/* Match Score Indicator for Top Matches */}
      {view === 'top' && (
        <div style={{
          padding: '16px',
          background: 'rgba(215, 181, 109, 0.08)',
          borderBottom: '1px solid rgba(165, 124, 51, 0.3)',
          margin: '16px',
          borderRadius: '12px',
          border: '1px solid #A57C33'
        }}>
          <div style={{
            color: '#D7B56D',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px'
          }}>
            <Star size={16} fill="#D7B56D" style={{ marginTop: '2px', flexShrink: 0 }} />
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Match Score Explained</strong>
              AI-powered compatibility percentage based on shared interests, music taste, favorite spots, age compatibility, location proximity, and profile engagement
            </div>
          </div>
        </div>
      )}

      {/* Matches Grid */}
      <div style={{
        padding: '16px',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '16px'
      }}>
        {matches.map((match) => (
          <div
            key={match.id}
            onClick={() => navigate(`/user/${match.id}`)}
            style={{
              background: 'rgba(215, 181, 109, 0.08)',
              borderRadius: '16px',
              overflow: 'hidden',
              cursor: 'pointer',
              border: '1px solid #A57C33',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              position: 'relative'
            }}
          >
            {/* Match Score Badge for Top Matches */}
            {view === 'top' && match.match_score !== undefined && (
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'linear-gradient(135deg, #D7B56D, #A57C33)',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#2C1B13',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                zIndex: 2
              }}>
                <Sparkles size={12} />
                {Math.round(match.match_score)}%
              </div>
            )}

            {/* Income Diamond Badge */}
            {renderIncomeBadge(match.income_verified)}

            {/* Profile Image */}
            <div style={{ position: 'relative' }}>
              <img
                src={match.photo}
                alt={match.name}
                style={{
                  width: '100%',
                  height: '220px',
                  objectFit: 'cover'
                }}
              />
              {match.online && (
                <div style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '8px',
                  width: '12px',
                  height: '12px',
                  background: '#50C878',
                  borderRadius: '50%',
                  border: '2px solid #2C1B13',
                  boxShadow: '0 0 8px rgba(80, 200, 120, 0.6)'
                }} />
              )}
            </div>

            {/* Profile Info */}
            <div style={{ padding: '12px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '4px',
                flexWrap: 'wrap'
              }}>
                <span style={{
                  color: '#E6D7C3',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  fontFamily: 'Playfair Display, serif'
                }}>
                  {getFirstName(match.name)}
                </span>
                {match.verified && (
                  <CheckCircle size={16} style={{ color: '#6A9BD8' }} />
                )}
                {match.elite && (
                  <Award size={16} style={{ color: '#D7B56D' }} />
                )}
                {match.new && (
                  <span style={{
                    background: '#6A1F28',
                    color: '#E6D7C3',
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}>
                    NEW
                  </span>
                )}
              </div>

              <div style={{
                color: '#A57C33',
                fontSize: '14px',
                marginBottom: '8px'
              }}>
                {match.age && `${match.age}, `}{match.occupation || 'Professional'}
              </div>

              {view === 'nearby' && match.distance !== undefined && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#D7B56D',
                  fontSize: '13px'
                }}>
                  <MapPin size={14} />
                  {match.distance < 0.1 ? 'Less than 100m' : `${match.distance.toFixed(1)} mi away`}
                </div>
              )}

              {match.bio && (
                <p style={{
                  color: '#E6D7C3',
                  fontSize: '13px',
                  marginTop: '8px',
                  lineHeight: '1.4',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {match.bio}
                </p>
              )}

              {/* Action Buttons - Message & Send Gift */}
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                marginTop: '12px' 
              }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/chat/${match.id}`, { state: { user: match } });
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#D7B56D',
                    color: '#2C1B13',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}
                >
                  <MessageCircle size={14} />
                  Message
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/send-gift/${match.id}`, { state: { recipient: match } });
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: 'rgba(215, 181, 109, 0.15)',
                    color: '#D7B56D',
                    border: '1px solid #D7B56D',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}
                >
                  <Gift size={14} />
                  Gift
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {matches.length === 0 && (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: '#A57C33'
        }}>
          No matches found. Check back soon!
        </div>
      )}
    </div>
  );
};

export default MatchesPage;
